import { updateTenantCreditSettings } from "../../api";
import { Button, NumberInput, Group, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTenantStore } from "./tenantStore";

// 1 credit = $0.000001
const CREDITS_PER_USD = 1_000_000;
const AUTO_TOP_UP_INTERVAL_MS = 24 * 60 * 60 * 1000;

const toUsd = (credits) => (credits == null ? "" : credits / CREDITS_PER_USD);
// Empty or zero turns the setting off
const toCredits = (usd) =>
  usd === "" || usd == null || Number(usd) <= 0
    ? null
    : Math.round(Number(usd) * CREDITS_PER_USD);

export default function CreditSettingsForm({ tenant }) {
  const [isLoading, setIsLoading] = useState(false);
  const updateTenantStore = useTenantStore((state) => state.updateTenant);

  // Controlled, so the top-up field can follow the threshold field
  const form = useForm({
    initialValues: {
      thresholdUsd: toUsd(tenant.lowBalanceThresholdCredits),
      topUpUsd: toUsd(tenant.autoTopUpCredits),
    },
  });

  const hasThreshold = toCredits(form.values.thresholdUsd) != null;
  const lastTopUp = tenant.lastAutoTopUpAt
    ? new Date(tenant.lastAutoTopUpAt)
    : null;

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        const lowBalanceThresholdCredits = toCredits(values.thresholdUsd);
        setIsLoading(true);
        updateTenantCreditSettings(tenant.id, {
          lowBalanceThresholdCredits,
          autoTopUpCredits:
            lowBalanceThresholdCredits == null
              ? null
              : toCredits(values.topUpUsd),
        })
          .then((data) => {
            updateTenantStore(tenant.id, data);
            notifications.show({
              title: "Credit settings saved",
              color: "green",
            });
            setIsLoading(false);
          })
          .catch((error) => {
            notifications.show({
              title: "Failed to save credit settings",
              message: error.message,
              color: "red",
            });
            setIsLoading(false);
          });
      })}
    >
      <Group align="flex-start" grow>
        <NumberInput
          label="Low-balance threshold"
          description="Below this balance, top up or warn. Empty = off."
          placeholder="Off"
          prefix="$"
          decimalScale={2}
          thousandSeparator
          min={0}
          {...form.getInputProps("thresholdUsd")}
        />
        <NumberInput
          label="Automatic top-up"
          description="Added below the threshold, at most once per 24 h. Empty = warn only."
          placeholder="Off"
          prefix="$"
          decimalScale={2}
          thousandSeparator
          min={0}
          disabled={!hasThreshold}
          {...form.getInputProps("topUpUsd")}
        />
      </Group>
      <Text size="sm" c="dimmed" mt="xs">
        A top-up is not charged. You get an email for each top-up; send the
        invoice by hand.
      </Text>
      {lastTopUp && (
        <Text size="sm" mt="xs">
          Last automatic top-up: {lastTopUp.toLocaleString()} · next possible
          after{" "}
          {new Date(
            lastTopUp.getTime() + AUTO_TOP_UP_INTERVAL_MS
          ).toLocaleString()}
        </Text>
      )}
      <Button mt="sm" disabled={isLoading} type="submit">
        Save
      </Button>
    </form>
  );
}
